import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { FiTrash2, FiMinus, FiPlus, FiTag, FiTruck, FiShield } from 'react-icons/fi';

const Cart = () => {
  const { 
    cartItems, 
    updateQuantity, 
    removeFromCart, 
    getSubtotal, 
    getCartTotal, 
    applyCoupon, 
    removeCoupon, 
    couponCode, 
    discount 
  } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const finalTotal = getCartTotal();

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;

    setApplyingCoupon(true);
    setCouponError('');
    try {
      const result = await applyCoupon(couponInput.toUpperCase());
      if (result.success) {
        setCouponError('');
        setCouponInput('');
      } else {
        setCouponError(result.message);
      }
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    removeCoupon();
    setCouponError('');
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/checkout');
      return;
    }
    navigate('/checkout');
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🛒</div>
          <h2 className="text-2xl font-bold mb-4">Your cart is empty</h2>
          <p className="text-gray-600 mb-8">Add some items to get started</p>
          <Link to="/products" className="btn-primary">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-8">Shopping Cart ({cartItems.length} items)</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {cartItems.map((item) => (
            <div key={item.cartId} className="card p-6">
              <div className="flex items-center space-x-4">
                {/* Product Image */}
                <div className="w-24 aspect-[4/5] flex-shrink-0 overflow-hidden rounded-lg">
                  <img
                    src={api.utils.getImageUrl(item.image || item.images?.[0], { width: 1200, height: 1500, fit: 'cover' })}
                    alt={item.name}
                    className="w-full h-full object-cover object-center"
                  />
                </div>

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {item.name}
                  </h3>
                  <p className="text-gray-600">Size: {item.size}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-lg font-bold">
                      ₹{item.price.toLocaleString()}
                    </span>
                    {item.originalPrice && (
                      <span className="text-sm text-gray-500 line-through">
                        ₹{item.originalPrice.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Quantity Controls */}
                <div className="flex items-center space-x-3">
                  <div className="flex items-center border rounded-lg">
                    <button
                      onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                      className="p-2 hover:bg-gray-100"
                    >
                      <FiMinus size={16} />
                    </button>
                    <span className="px-4 py-2 font-medium">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                      className="p-2 hover:bg-gray-100"
                    >
                      <FiPlus size={16} />
                    </button>
                  </div>

                  <button
                    onClick={() => removeFromCart(item.cartId)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="space-y-6">
          {/* Coupon Section */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <FiTag className="mr-2" />
              Apply Coupon
            </h3>
            
            {couponCode ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-green-800">Coupon Applied!</p>
                    <p className="text-sm text-green-600">
                      {couponCode} - ₹{discount} off
                    </p>
                  </div>
                  <button
                    onClick={handleRemoveCoupon}
                    className="text-green-600 hover:text-green-800"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApplyCoupon();
                      }
                    }}
                    className="input-field flex-1"
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={applyingCoupon}
                    className="btn-secondary whitespace-nowrap disabled:opacity-50"
                  >
                    {applyingCoupon ? 'Applying...' : 'Apply'}
                  </button>
                </div>
                {couponError && (
                  <p className="text-red-600 text-sm mt-2">{couponError}</p>
                )}
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>₹{getSubtotal().toLocaleString()}</span>
              </div>
              
              {discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>-₹{discount}</span>
                </div>
              )}
              
              <div className="border-t pt-3">
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <span>₹{finalTotal.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleCheckout}
              className="btn-primary w-full mt-6 text-lg py-4"
            >
              {isAuthenticated ? 'Proceed to Checkout' : 'Login to Checkout'}
            </button>

            {!isAuthenticated && (
              <p className="text-sm text-gray-600 text-center mt-3">
                You need to login to place an order
              </p>
            )}
          </div>

          {/* Security Features */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">Secure Shopping</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <FiShield className="text-green-600" />
                <span className="text-sm">100% Secure Payment</span>
              </div>
              <div className="flex items-center space-x-3">
                <FiTruck className="text-green-600" />
                <span className="text-sm">Fast & Free Delivery</span>
              </div>
              <div className="flex items-center space-x-3">
                <FiTag className="text-green-600" />
                <span className="text-sm">Easy Returns & Refunds</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Continue Shopping */}
      <div className="mt-12 text-center">
        <Link to="/products" className="btn-secondary">
          Continue Shopping
        </Link>
      </div>
    </div>
  );
};

export default Cart; 